import { createContext, useContext, type ReactNode } from 'react';
import type { BrokerAdapters } from './types';

const AdapterContext = createContext<BrokerAdapters | null>(null);

interface AdapterProviderProps {
  readonly adapters: BrokerAdapters;
  readonly children: ReactNode;
}

/**
 * Supplies the host capabilities the broker experience runs on.
 *
 * Mounted once, by the console-owned mount. Features reach it through
 * {@link useAdapters} and never import concrete host code.
 */
export function AdapterProvider({ adapters, children }: AdapterProviderProps) {
  return (
    <AdapterContext.Provider value={adapters}>
      {children}
    </AdapterContext.Provider>
  );
}

/**
 * Throws when used outside an {@link AdapterProvider}.
 *
 * Failing loudly is deliberate: a missing provider means a feature is running
 * without a host, and every alternative — returning undefined, falling back to
 * a stub — turns that into a silent bug somewhere further away.
 */
export function useAdapters(): BrokerAdapters {
  const adapters = useContext(AdapterContext);

  if (adapters === null) {
    throw new Error(
      'useAdapters must be used within an AdapterProvider. The broker experience ' +
        'cannot reach the host without one.'
    );
  }

  return adapters;
}

/**
 * The adapters if an {@link AdapterProvider} is above, otherwise null. For a component that also works outside the
 * broker experience, given what it needs another way.
 */
export function useOptionalAdapters(): BrokerAdapters | null {
  return useContext(AdapterContext);
}
